// Location helpers — safe, backward compatible.
//
// Every service keeps `location` (plain string, as before) and optionally
// `locationDetail` (accurate map info). Old events without `locationDetail`
// keep working: helpers fall back to the plain string.

export const getServiceLocationText = (service) => {
  if (!service) return '';
  const detailAddress =
    service.locationDetail && service.locationDetail.address
      ? String(service.locationDetail.address)
      : '';
  const plain = service.location ? String(service.location) : '';
  return (detailAddress || plain).trim();
};

// Trailing segments that are stripped for compact display (view/table/card).
// The form keeps + stores the full address (state/country help disambiguate
// suggestions); only the display shortens it to the specific place.
const STRIPPABLE_PLACES = new Set(
  [
    // Countries (common + region)
    'india', 'united states', 'united states of america', 'usa', 'united kingdom', 'uk',
    'united arab emirates', 'uae', 'saudi arabia', 'qatar', 'kuwait', 'oman', 'bahrain',
    'canada', 'australia', 'new zealand', 'singapore', 'malaysia', 'thailand', 'vietnam',
    'indonesia', 'philippines', 'japan', 'south korea', 'korea', 'china', 'nepal', 'bhutan',
    'bangladesh', 'sri lanka', 'maldives', 'myanmar', 'pakistan', 'afghanistan', 'germany',
    'france', 'italy', 'spain', 'netherlands', 'russia', 'brazil', 'south africa',
    // Indian states
    'andhra pradesh', 'arunachal pradesh', 'assam', 'bihar', 'chhattisgarh', 'goa',
    'gujarat', 'haryana', 'himachal pradesh', 'jharkhand', 'karnataka', 'kerala',
    'madhya pradesh', 'maharashtra', 'manipur', 'meghalaya', 'mizoram', 'nagaland',
    'odisha', 'orissa', 'punjab', 'rajasthan', 'sikkim', 'tamil nadu', 'telangana',
    'tripura', 'uttar pradesh', 'uttarakhand', 'uttaranchal', 'west bengal',
    // Indian union territories (+ common aliases)
    'andaman and nicobar islands', 'chandigarh',
    'dadra and nagar haveli and daman and diu', 'dadra and nagar haveli', 'daman and diu',
    'delhi', 'nct', 'national capital territory of delhi',
    'jammu and kashmir', 'ladakh', 'lakshadweep', 'puducherry', 'pondicherry',
  ].map((s) => s.toLowerCase())
);

// District-level address segments (matched by pattern — district names are
// unbounded, e.g. "Bengaluru Urban", "Mysuru district", "Mysuru Taluk").
const DISTRICT_PART_PATTERN = /\b(urban|rural|district|division|zilla|jilla|mandal|taluk|taluka|tehsil|tahsil|sub[-\s]?division)\s*$/i;

// Shorten a full address ("Forum Mall, Whitefield, Bengaluru Urban, Bengaluru,
// Karnataka, India") to the specific place ("Forum Mall, Whitefield, Bengaluru")
// for view/table/card. Drops country, state, district and postcode segments.
export const shortenLocationAddress = (address) => {
  let parts = String(address || '')
    .split(',')
    .map((p) => String(p || '').trim())
    .filter(Boolean);
  if (parts.length <= 1) return parts.join(', ');
  // Drop trailing country / state / postcode segments (always keep >= 1 part).
  while (parts.length > 1) {
    const last = parts[parts.length - 1];
    // Pure postcode segment ("560001").
    if (/^\d{5,6}(-\d+)?$/.test(last)) {
      parts.pop();
      continue;
    }
    // State with attached postcode ("Karnataka 560001").
    const withoutPostcode = last.replace(/\s+\d{5,6}(-\d+)?$/, '').trim().toLowerCase();
    if (STRIPPABLE_PLACES.has(last.toLowerCase()) || STRIPPABLE_PLACES.has(withoutPostcode)) {
      parts.pop();
      continue;
    }
    break;
  }
  // Drop district-level segments anywhere in the address ("Bengaluru Urban",
  // "Mysuru district", "Mysuru Taluk", "Kolar division") — district names are
  // unbounded so they are matched by pattern instead of a list. The first
  // segment (the specific place) is always kept.
  const withoutDistricts = parts.filter((p, i) => i === 0 || !DISTRICT_PART_PATTERN.test(p));
  parts = withoutDistricts.length > 0 ? withoutDistricts : parts.slice(0, 1);
  // Cap length so long geocoded addresses stay compact (keep the specific end).
  return parts.slice(0, 3).join(', ');
};

// Whether this service's location text should act as a map link.
// Defaults to ON (true) so older events without the flag keep working;
// the event form's "Map" toggle writes an explicit boolean per service.
export const isServiceMapLinkEnabled = (service) => {
  if (!service) return true;
  return service.locationMapEnabled !== false;
};

// Compact display text for event view/table/card (no state/country).
// Map URLs intentionally keep using the full text for pin/search accuracy.
export const getServiceShortLocationText = (service) => {
  if (!service) return '';
  const full =
    (service.locationDetail && service.locationDetail.address
      ? String(service.locationDetail.address)
      : '') || (service.location ? String(service.location) : '');
  const short = shortenLocationAddress(full);
  return (short || full).trim();
};

// Build a Google Maps link that works without any API key.
// - If lat/lng known -> exact pin link (most accurate for team).
// - Else if address text known -> search link (still opens correct place).
// - Else empty string.
export const getServiceMapUrl = (service) => {
  if (!service) return '';
  const detail = service.locationDetail || {};
  if (detail.mapUrl && String(detail.mapUrl).trim()) return String(detail.mapUrl).trim();

  const lat = Number(detail.lat);
  const lng = Number(detail.lng);
  if (Number.isFinite(lat) && Number.isFinite(lng) && (lat !== 0 || lng !== 0)) {
    return `https://www.google.com/maps?q=${lat},${lng}`;
  }

  const text = getServiceLocationText(service);
  if (!text) return '';
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(text)}`;
};

// Normalize whatever the picker returns into a small storable object.
// Returns null when there is no usable location (so we don't store junk).
export const normalizeLocationDetail = (input) => {
  if (!input) return null;
  if (typeof input === 'string') {
    const address = input.trim();
    if (!address) return null;
    return {
      address,
      mapUrl: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`,
      lat: null,
      lng: null,
      placeId: '',
      source: 'manual',
    };
  }
  const address = String(input.address || input.displayName || '').trim();
  if (!address && (input.lat == null || input.lng == null)) return null;
  const lat = input.lat !== undefined && input.lat !== null && input.lat !== '' ? Number(input.lat) : null;
  const lng = input.lng !== undefined && input.lng !== null && input.lng !== '' ? Number(input.lng) : null;
  const hasCoords = Number.isFinite(lat) && Number.isFinite(lng);
  return {
    address: address || (hasCoords ? `${lat},${lng}` : ''),
    mapUrl:
      String(input.mapUrl || '').trim() ||
      (hasCoords
        ? `https://www.google.com/maps?q=${lat},${lng}`
        : address
          ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`
          : ''),
    lat: hasCoords ? lat : null,
    lng: hasCoords ? lng : null,
    placeId: String(input.placeId || ''),
    source: String(input.source || 'search'),
  };
};
